import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { billsApi } from './api';
import type {
  BillListFilters,
  BillsListResponse,
  BillWithItems,
  InitiateReturnInput,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useBillsList(
  filters: BillListFilters,
): UseQueryResult<BillsListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['bills', tenantId, 'list', filters],
    queryFn: () => billsApi.list(filters),
    staleTime: 15_000,
    // Keep showing the previous page while a new one loads — avoids a
    // flash of skeleton on every filter change.
    placeholderData: (prev) => prev,
  });
}

export function useBill(id: string | undefined): UseQueryResult<BillWithItems> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['bills', tenantId, 'detail', id],
    queryFn: () => billsApi.getById(id!),
    enabled: Boolean(id),
    // Bills are immutable after VOID/RETURN; refetch is for the rare race
    // where the page renders before a concurrent return finishes.
    staleTime: 15_000,
  });
}

export function useVoidBill() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billsApi.void(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['bills', tenantId] });
      qc.invalidateQueries({ queryKey: ['bills', tenantId, 'detail', id] });
      // The dashboard's RecentBills widget reads from a different cache key.
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useInitiateReturn() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: InitiateReturnInput }) =>
      billsApi.initiateReturn(id, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['bills', tenantId] });
      qc.invalidateQueries({ queryKey: ['bills', tenantId, 'detail', vars.id] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
